import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, Eye, EyeOff, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_propmatch-7/artifacts/y6a3qiy4_LogotipoFinal_IkonicDeals%28web%29.jpg";
const API = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: ""
  });
  const [errors, setErrors] = useState({});

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        await axios.get(`${API}/api/auth/validate-reset-token/${token}`);
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setValidating(false);
      }
    };
    validateToken();
  }, [token]);

  const validateForm = () => {
    const newErrors = {};
    
    if (formData.password.length < 8) {
      newErrors.password = "La contraseña debe tener al menos 8 caracteres";
    }
    
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Las contraseñas no coinciden";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      await axios.post(`${API}/api/auth/reset-password`, {
        token,
        password: formData.password
      });
      setSuccess(true);
      toast.success("Contraseña actualizada correctamente");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al restablecer la contraseña");
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" data-testid="reset-password-page">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin mx-auto mb-4" />
          <p className="text-[#A3A3A3]">Verificando enlace...</p>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" data-testid="reset-password-page">
        <div className="w-full max-w-md text-center">
          <img src={LOGO_URL} alt="Ikonic Deals" className="h-16 mx-auto mb-8" />
          
          <div className="bg-[#121212] border border-white/5 p-8">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="text-2xl text-white mb-4">Enlace inválido o expirado</h1>
            <p className="text-[#A3A3A3] mb-8">
              Este enlace de recuperación ya no es válido. Por favor, solicita uno nuevo.
            </p>
            
            <Link 
              to="/panel-gestion-8k2m/recuperar"
              className="inline-block w-full"
            >
              <Button className="w-full h-12 bg-[#D4AF37] text-black hover:bg-[#FFE082] font-medium">
                Solicitar nuevo enlace
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" data-testid="reset-password-page">
        <div className="w-full max-w-md text-center">
          <img src={LOGO_URL} alt="Ikonic Deals" className="h-16 mx-auto mb-8" />
          
          <div className="bg-[#121212] border border-white/5 p-8">
            <CheckCircle className="w-16 h-16 text-[#D4AF37] mx-auto mb-6" />
            <h1 className="text-2xl text-white mb-4">¡Contraseña actualizada!</h1>
            <p className="text-[#A3A3A3] mb-8">
              Tu contraseña ha sido restablecida correctamente. Ya puedes iniciar sesión con tu nueva contraseña.
            </p>
            
            <Button 
              onClick={() => navigate("/panel-gestion-8k2m/acceso")}
              className="w-full h-12 bg-[#D4AF37] text-black hover:bg-[#FFE082] font-medium"
              data-testid="go-to-login"
            >
              Ir a iniciar sesión
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Ikonic Deals" className="h-16 mx-auto mb-6" />
          <h1 className="text-2xl text-white">Nueva contraseña</h1>
          <p className="text-[#A3A3A3] mt-2">
            Ingresa tu nueva contraseña
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#121212] border border-white/5 p-8 space-y-6">
          <div>
            <label className="block text-sm text-[#A3A3A3] mb-2">Nueva contraseña</label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Mínimo 8 caracteres"
                className={`h-12 bg-black/40 border-white/10 text-white placeholder:text-white/40 pr-12 ${errors.password ? 'border-red-500' : ''}`}
                data-testid="reset-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password}</p>
            )}
          </div>

          <div>
            <label className="block text-sm text-[#A3A3A3] mb-2">Confirmar contraseña</label>
            <div className="relative">
              <Input
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Repite tu contraseña"
                className={`h-12 bg-black/40 border-white/10 text-white placeholder:text-white/40 pr-12 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                data-testid="reset-confirm-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#D4AF37] text-black hover:bg-[#FFE082] font-medium"
            data-testid="reset-submit"
          >
            {loading ? <Loader2 className="animate-spin" size={20} /> : "Restablecer contraseña"}
          </Button>
        </form>

        <p className="text-center mt-6 text-[#525252] text-sm">
          <Link 
            to="/panel-gestion-8k2m/acceso" 
            className="hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft size={16} />
            Volver al inicio de sesión
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
